import React, { useEffect } from 'react';
import {
  ArrowRight,
  Check,
  ChevronRight,
  Cloud,
  Code2,
  CreditCard,
  Database,
  Globe,
  Layers,
  MessageCircle,
  Monitor,
  Package,
  Palette,
  RefreshCw,
  Rocket,
  Server,
  Shield,
  Smartphone,
  Sparkles,
  Store,
  Users,
  Zap
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useLanguage } from '../context/LanguageContext';
import { getWsjCalDemoBookingUrl } from '@/lib/wsjCalUrl';
import { getWsjCommercialWhatsappUrl } from '@/lib/wsjCommercialWhatsapp';
import { MobileAppsHeroMockups } from '../components/mobile-apps/MobileAppsHeroMockups';
import { MobileAppsPlansSection } from '../components/mobile-apps/MobileAppsPlansSection';
import { MobileAppsRealProjectsSection } from '../components/mobile-apps/MobileAppsRealProjectsSection';

const problemIcons = [Users, RefreshCw, Monitor, Package, Zap, Shield, Layers, Code2];
const useCaseIcons = [Monitor, Package, Globe, Users, Layers, Monitor, Zap, CreditCard, Shield, Package, Sparkles, Code2];
const deliverableIcons = [
  Palette, Smartphone, Globe, Shield, Database, Code2, Zap, Monitor, Layers, Store, Check, MessageCircle
];
const costIcons = [Store, Smartphone, Server, Database, Cloud, RefreshCw];
const processIcons = [MessageCircle, Layers, Palette, Code2, Rocket];

const technologies = ['React Native', 'Expo', 'APIs', 'Firebase', 'Node.js', 'Spring Boot', 'Push notifications'];

const MobileApps = () => {
  const { t, language } = useLanguage();
  const p = t.mobileAppsPage;
  const quoteUrl = getWsjCalDemoBookingUrl();

  useEffect(() => {
    const prev = document.title;
    document.title = p.metaTitle;
    return () => {
      document.title = prev;
    };
  }, [p.metaTitle]);

  const openWhatsapp = () => {
    window.open(getWsjCommercialWhatsappUrl(p.whatsappMessage), '_blank', 'noopener,noreferrer');
  };

  const scrollToPlans = () => {
    document.getElementById('mobile-apps-plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen w-full min-w-0 max-w-full overflow-x-hidden">
      <Header />
      <main className="relative overflow-x-hidden pt-24 sm:pt-28 pb-8">
        <div className="absolute inset-0 tech-grid opacity-[0.18] pointer-events-none" />
        <div className="absolute top-24 left-1/4 w-[min(420px,calc(100vw-2rem))] max-w-[100vw] h-[min(420px,70vw)] bg-sky-500/[0.07] rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-40 right-0 w-[min(380px,calc(100vw-2rem))] max-w-[100vw] h-[min(380px,65vw)] bg-indigo-500/[0.06] rounded-full blur-[90px] pointer-events-none" />

        <div className="relative z-10 container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8">
          {/* HERO */}
          <section className="min-w-0 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 xl:gap-16 items-center mb-16 sm:mb-24 lg:mb-28">
            <div className="text-center lg:text-left pt-2 min-w-0">
              <div className="inline-flex max-w-full min-w-0 items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-xs font-semibold tracking-wide text-sky-200/90 mb-5">
                <Smartphone className="w-3.5 h-3.5 text-sky-400 shrink-0" aria-hidden />
                <span className="break-words text-center">{p.badge}</span>
              </div>
              <h1 className="text-[1.65rem] sm:text-4xl lg:text-[2.65rem] xl:text-5xl font-black text-white leading-[1.12] tracking-tight mb-5 break-words">
                {p.heroTitle}
              </h1>
              <p className="text-base sm:text-lg text-white/55 font-light leading-relaxed max-w-xl mx-auto lg:mx-0 mb-6 break-words">
                {p.heroSubtitle}
              </p>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-8">
                {p.heroBadges.map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium text-white/65 backdrop-blur-sm"
                  >
                    {badge}
                  </span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center lg:justify-start gap-3">
                <Button asChild size="lg" className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-lg shadow-amber-900/25 h-12 px-6">
                  <a href={quoteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
                    {p.btnQuote}
                    <ArrowRight className="w-4 h-4" aria-hidden />
                  </a>
                </Button>
                <Button type="button" size="lg" variant="outline" className="rounded-xl border-emerald-500/35 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15 h-12 px-6" onClick={openWhatsapp}>
                  <MessageCircle className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                  {p.btnWhatsapp}
                </Button>
                <Button type="button" size="lg" variant="outline" className="rounded-xl border-white/20 bg-white/[0.04] text-white hover:bg-white/10 h-12 px-6" onClick={scrollToPlans}>
                  {p.btnPlans}
                  <ChevronRight className="w-4 h-4 ml-1" aria-hidden />
                </Button>
              </div>
            </div>
            <div className="mt-10 lg:mt-0 relative min-w-0">
              <MobileAppsHeroMockups mock={p.heroMock} />
            </div>
          </section>

          {/* PROBLEMAS */}
          <section id="mobile-apps-problems" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-10 sm:mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{p.sectionProblemsTitle}</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-sky-500 to-indigo-500 mx-auto rounded-full mt-6" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8">
              {p.problemItems.map((item, i) => {
                const Icon = problemIcons[i] || Zap;
                return (
                  <div key={item.title} className="rounded-2xl border border-white/[0.08] bg-slate-950/40 backdrop-blur-md p-5 sm:p-6 hover:border-sky-500/20 transition-colors duration-300">
                    <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-sky-400" aria-hidden />
                    </div>
                    <h3 className="text-sm font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-white/45 font-light leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-base sm:text-lg text-sky-200/80 font-light max-w-3xl mx-auto leading-relaxed break-words">{p.sectionProblemsClose}</p>
          </section>

          {/* HÍBRIDO */}
          <section id="mobile-apps-hybrid" className="max-w-5xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="rounded-[2rem] border border-indigo-500/20 bg-gradient-to-br from-indigo-500/[0.08] via-slate-950/70 to-sky-950/40 backdrop-blur-xl p-6 sm:p-10 lg:p-12">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{p.sectionHybridTitle}</h2>
                <p className="text-white/50 mt-4 max-w-2xl mx-auto font-light leading-relaxed break-words">{p.sectionHybridLead}</p>
              </div>
              <p className="text-sm text-white/55 font-light leading-relaxed text-center max-w-3xl mx-auto mb-8 break-words">{p.sectionHybridDesc}</p>
              <div className="grid md:grid-cols-3 gap-4 sm:gap-5 mb-8">
                <div className="rounded-2xl border border-white/[0.08] bg-slate-950/50 p-5 sm:p-6">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center mb-4">
                    <Smartphone className="w-5 h-5 text-emerald-400" aria-hidden />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">{p.hybridAndroidTitle}</h3>
                  <p className="text-sm text-white/45 font-light leading-relaxed">{p.hybridAndroidDesc}</p>
                </div>
                <div className="rounded-2xl border border-white/[0.08] bg-slate-950/50 p-5 sm:p-6">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-500/25 flex items-center justify-center mb-4">
                    <Store className="w-5 h-5 text-sky-400" aria-hidden />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">{p.hybridIosTitle}</h3>
                  <p className="text-sm text-white/45 font-light leading-relaxed">{p.hybridIosDesc}</p>
                </div>
                <div className="rounded-2xl border border-white/[0.08] bg-slate-950/50 p-5 sm:p-6">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center mb-4">
                    <Code2 className="w-5 h-5 text-indigo-400" aria-hidden />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">{p.hybridBackendTitle}</h3>
                  <p className="text-sm text-white/45 font-light leading-relaxed">{p.hybridBackendDesc}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                {technologies.map((tech) => (
                  <span key={tech} className="inline-flex rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium text-white/60">{tech}</span>
                ))}
              </div>
              <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-4 sm:px-6 sm:py-5">
                <p className="text-sm text-amber-100/75 font-light leading-relaxed text-center break-words">{p.hybridNote}</p>
              </div>
            </div>
          </section>

          {/* CASOS DE USO */}
          <section id="mobile-apps-usecases" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{p.sectionUseCasesTitle}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionUseCasesLead}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {p.useCases.map((item, i) => {
                const Icon = useCaseIcons[i] || Sparkles;
                return (
                  <div key={item.title} className="group rounded-2xl border border-white/[0.08] bg-slate-950/35 backdrop-blur-md p-5 hover:border-indigo-500/25 transition-all duration-300">
                    <div className="w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-500/20 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                      <Icon className="w-4 h-4 text-indigo-400" aria-hidden />
                    </div>
                    <h3 className="text-sm font-bold text-white mb-1.5 break-words">{item.title}</h3>
                    <p className="text-xs text-white/45 font-light leading-relaxed break-words">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ENTREGÁVEIS */}
          <section id="mobile-apps-deliverables" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{p.sectionDeliverablesTitle}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionDeliverablesLead}</p>
              <div className="w-20 h-1 bg-gradient-to-r from-sky-500 to-indigo-500 mx-auto rounded-full mt-6" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {p.deliverables.map((item, i) => {
                const Icon = deliverableIcons[i] || Sparkles;
                return (
                  <div key={item.title} className="rounded-2xl border border-white/[0.08] bg-slate-950/35 backdrop-blur-md p-5 sm:p-6 hover:border-sky-500/25 transition-all duration-300">
                    <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-500/20 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-sky-400" aria-hidden />
                    </div>
                    <h3 className="text-sm font-bold text-white mb-2 break-words">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-white/45 font-light leading-relaxed break-words">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* CUSTOS EXTERNOS */}
          <section id="mobile-apps-costs" className="max-w-5xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{p.sectionCostsTitle}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light leading-relaxed break-words">{p.sectionCostsIntro}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-6">
              {p.costItems.map((item, i) => {
                const Icon = costIcons[i] || Server;
                return (
                  <div key={item.title} className="rounded-2xl border border-white/[0.08] bg-slate-950/40 backdrop-blur-md p-5 hover:border-amber-500/20 transition-colors duration-300">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-amber-400" aria-hidden />
                    </div>
                    <h3 className="text-sm font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-white/45 font-light leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
            <div className="rounded-2xl border border-sky-500/20 bg-sky-500/[0.06] px-4 py-4 sm:px-6 sm:py-5">
              <p className="text-sm text-sky-100/75 font-light leading-relaxed text-center break-words">{p.costsHighlight}</p>
            </div>
            <p className="text-center text-sm text-white/40 font-light mt-4 max-w-2xl mx-auto leading-relaxed">{p.sectionCostsLead}</p>
          </section>

          {/* PROCESSO */}
          <section id="mobile-apps-process" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white break-words">{p.sectionProcessTitle}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionProcessLead}</p>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
              {p.processSteps.map((step, i) => {
                const Icon = processIcons[i] || Sparkles;
                return (
                  <div key={step.title} className="relative min-w-0 rounded-2xl border border-white/[0.08] bg-slate-950/50 backdrop-blur-md p-5 pt-10 text-center lg:text-left hover:border-sky-500/25 transition-colors duration-300">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 lg:left-5 lg:translate-x-0 w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white font-black text-sm flex items-center justify-center shadow-lg shadow-sky-900/30">
                      {i + 1}
                    </div>
                    <Icon className="w-6 h-6 text-sky-400/90 mx-auto lg:mx-0 mb-3 mt-2" aria-hidden />
                    <h3 className="text-sm font-bold text-white mb-2 leading-snug break-words">{step.title}</h3>
                    <p className="text-xs text-white/45 leading-relaxed font-light break-words">{step.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          <MobileAppsPlansSection language={language} copy={p} quoteUrl={quoteUrl} onPremiumContact={openWhatsapp} />

          <MobileAppsRealProjectsSection copy={p} />

          {/* FAQ */}
          <section id="mobile-apps-faq" className="max-w-3xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-4xl font-black text-white break-words">{p.sectionFaqTitle}</h2>
              <p className="text-white/50 mt-3 font-light break-words">{p.sectionFaqLead}</p>
            </div>
            <Accordion type="single" collapsible className="rounded-2xl border border-white/[0.08] bg-slate-950/40 backdrop-blur-md px-4 sm:px-6 divide-y divide-white/[0.06]">
              {p.faqItems.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className="border-white/[0.06]">
                  <AccordionTrigger className="text-white hover:text-sky-300 hover:no-underline text-left font-semibold py-5">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-white/50 font-light leading-relaxed pb-5">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* CTA FINAL */}
          <section id="mobile-apps-cta" className="max-w-5xl mx-auto mb-16 sm:mb-20 scroll-mt-28 relative rounded-[2rem] border border-white/[0.08] overflow-hidden shadow-[0_40px_120px_-48px_rgba(56,189,248,0.28)]">
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500/[0.14] via-slate-950/95 to-indigo-950/80 pointer-events-none" aria-hidden />
            <div className="relative px-6 py-10 sm:px-12 sm:py-14 lg:px-16 lg:py-16 text-center">
              <h2 className="text-2xl sm:text-4xl lg:text-[2.35rem] font-black text-white tracking-tight mb-4 max-w-3xl mx-auto leading-tight break-words px-1">{p.finalCtaTitle}</h2>
              <p className="text-white/55 font-light mb-10 max-w-2xl mx-auto leading-relaxed text-base sm:text-lg break-words px-1">{p.finalCtaSubtitle}</p>
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                <Button asChild size="lg" className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold px-8 h-12 shadow-lg shadow-amber-900/25">
                  <a href={quoteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                    {p.btnQuote}
                    <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
                  </a>
                </Button>
                <Button type="button" size="lg" variant="outline" className="rounded-xl border-emerald-500/40 bg-emerald-500/10 text-emerald-50 hover:bg-emerald-500/18 h-12 px-8 font-semibold" onClick={openWhatsapp}>
                  <MessageCircle className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                  {p.btnWhatsapp}
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MobileApps;
